
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { NgbModal, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { UserService } from '@services/core/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-roles',
  templateUrl: './roles.component.html',
  styleUrls: ["./roles.component.scss"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgbTooltipModule, NgSelectComponent],
  providers: [UserService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class UserRolesComponent implements OnInit {

  private _userService = inject(UserService)
  private _fb = inject(FormBuilder)
  private _modalService = inject(NgbModal)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  protected roles
  protected search = ""
  protected menuCounts: Record<string, number> = {}
  protected cloneSources: any[] = []
  protected cloneSourceId: string | null = null
  protected cloneTarget: any = null

  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""
  protected newRoleForm = this._fb.group({
    id: [null],
    name: ["", Validators.required],
    description: ["", Validators.required],
    enabled: [true],
  })

  ngOnInit(): void {
    const data = this.route.snapshot.data['rolesData']
    this.roles = data.roles
  }

  open(modal, role?) {
    if(role) this.newRoleForm.patchValue(role)
    this._modalService.open(modal, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `${reason}`;
    });
  }

  getRoles() {
    this._userService.getRoles().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles) => {
      this.refreshMenus(roles);
    });
  }

  private refreshMenus(roles: any) {
    this.roles = roles;
    this.loadMenuCounts(roles);
  }

  private loadMenuCounts(roles: any) {
    for (const role of (roles || [])) this.fetchMenuCount(role);
  }

  private fetchMenuCount(role: any) {
    this._userService.getMenuByRole(role.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menus: any) => {
      this.menuCounts = setMenuCount(this.menuCounts, role.id, menus);
    });
  }

  hasNoMenus(role: any): boolean {
    return this.menuCounts[role.id] === 0;
  }

  saveRole() {
    this._userService.updateRole(this.newRoleForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      this.refreshMenus(response)
      this.newRoleForm.reset();
      this.newRoleForm.patchValue({enabled: true})
    });
  }

  enabledRole(role){
    role.enabled = !role.enabled
    this._userService.updateRole(role).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      this.refreshMenus(response)
    });
  }

  moveUp(role: any, index: number) {
    if (index === 0) return
    this.roles[index] = this.roles[index - 1]
    this.roles[index - 1] = role
    this.saveRoleOrder()
  }

  moveDown(role: any, index: number) {
    if (index === this.roles.length - 1) return
    this.roles[index] = this.roles[index + 1]
    this.roles[index + 1] = role
    this.saveRoleOrder()
  }

  private saveRoleOrder() {
    this._userService.reorderRoles(roleOrderPayload(this.roles)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getRoles()
    })
  }

  openClone(modal: any, role: any) {
    this.cloneTarget = role
    this.cloneSources = filterCloneSources(this.roles, role)
    this.cloneSourceId = null
    this._modalService.open(modal, this.modalOptions)
  }

  confirmClone() {
    if (!readyToClone(this.cloneSourceId, this.cloneTarget)) return
    this._userService.cloneRoleMenus(this.cloneSourceId!, this.cloneTarget.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.afterClone()
    })
  }

  private afterClone() {
    this._modalService.dismissAll()
    this.getRoles()
  }

}

function roleOrderPayload(roles: any[]): any[] {
  const payload: any[] = []
  for (let i = 0; i < roles.length; i++) payload.push({ id: roles[i].id, index: i })
  return payload
}

function setMenuCount(counts: Record<string, number>, roleId: string, menus: any): Record<string, number> {
  return { ...counts, [roleId]: (menus || []).length };
}

function filterCloneSources(roles: any[], target: any): any[] {
  const out: any[] = []
  for (const role of (roles || [])) pushSource(out, role, target)
  return out
}

function pushSource(out: any[], role: any, target: any): void {
  if (role.id !== target.id) out.push(role)
}

function readyToClone(sourceId: string | null, target: any): boolean {
  if (!sourceId) return false;
  return target !== null && target !== undefined;
} 
